import { css } from '@emotion/react';
import { tokens } from '@domain-group/fe-brary';
import { media } from '../../styles/breakpoints-2026';
import {
  PAGE_MAX_WIDTH_2026,
  RAIL_WIDTH_2026,
  RAIL_WIDTH_LARGE_2026,
  pageWrapper2026,
  breadcrumbBar2026,
  breadcrumbs2026,
} from '../layout/layout.styles';
import { MOBILE_BANNER_HEIGHT } from '../agent-profile-agency-banner/agent-profile-agency-banner.style';

const { spacing, colors } = tokens;

const MAIN_COLUMN_MAX_WIDTH = 780; // Section_Overview width at the 1248 frame
const TABLET_INLINE_PHOTO_SIZE = 188; // Agent avatar next to Section_Overview at the 768 frame
const NARROW_TABLET_INLINE_PHOTO_SIZE = 124;
const BANNER_OVERLAP = '44px';
const SHEET_OVERLAP = '-40px';
const SHEET_RADIUS = '24px';
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const GUTTER_DESKTOP = spacing.xl;
const GUTTER_WIDE = spacing.xxl;

export const wrapper = pageWrapper2026;

export const breadcrumbBar = breadcrumbBar2026;

export const breadcrumbs = breadcrumbs2026;

export const mainContainer = css`
  box-sizing: border-box;
  width: 100%;
  max-width: ${PAGE_MAX_WIDTH_2026}px;
  margin: 0 auto;
`;

export const bannerSlot = css`
  ${media('<tablet')} {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  ${REDUCED_MOTION} {
    position: static;
  }
`;

export const bodyContainer = css`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    'photo'
    'details'
    'tiles'
    'content';
  row-gap: ${spacing.l};
  padding: ${spacing.l} ${spacing.m} ${spacing.xxl};

  ${media('<tablet')} {
    display: flex;
    flex-direction: column;
    padding: 0;
    row-gap: 0;
  }

  ${media('>=tablet', '<=tablet-narrow')} {
    grid-template-columns: minmax(0, 1fr) ${NARROW_TABLET_INLINE_PHOTO_SIZE}px;
    grid-template-areas:
      'details photo'
      'tiles tiles'
      'content content';
    column-gap: ${spacing.l};
    row-gap: ${spacing.m};
  }

  ${media('>tablet-narrow', '<desktop')} {
    grid-template-columns: minmax(0, 1fr) ${TABLET_INLINE_PHOTO_SIZE}px;
    grid-template-areas:
      'details photo'
      'tiles photo'
      'content content';
    column-gap: ${spacing.xl};
  }

  ${media('>=desktop')} {
    grid-template-columns:
      minmax(0, ${MAIN_COLUMN_MAX_WIDTH}px)
      ${RAIL_WIDTH_2026}px;
    grid-template-areas:
      'details sidebar'
      'tiles sidebar'
      'content sidebar';
    justify-content: space-between;
    // Don't add align-items: start - it shrinks the sidebar grid item (which
    // spans all 3 rows) to its content height, leaving position: sticky on
    // the contact card no room to move. Default (stretch) is required.
    column-gap: ${GUTTER_DESKTOP};
  }

  ${media('>lrg-desktop')} {
    grid-template-columns:
      minmax(0, ${MAIN_COLUMN_MAX_WIDTH}px)
      ${RAIL_WIDTH_LARGE_2026}px;
    column-gap: ${GUTTER_WIDE};
  }
`;

const sheetSection = css`
  ${media('<tablet')} {
    background-color: ${colors.neutral.background.default};
    padding-inline: ${spacing.m};
    position: relative;
    z-index: 1;
  }
`;

export const details = css`
  ${sheetSection}
  grid-area: details;
  min-width: 0;

  ${media('<tablet')} {
    border-radius: ${SHEET_RADIUS} ${SHEET_RADIUS} 0 0;
    margin-top: ${SHEET_OVERLAP};
    padding-top: ${spacing.l};
  }
`;

export const tiles = css`
  ${sheetSection}
  grid-area: tiles;
  min-width: 0;

  ${media('<tablet')} {
    padding-top: ${spacing.m};
  }

  ${media('>tablet-narrow')} {
    margin-top: calc(${spacing.m} - ${spacing.l});
  }
`;

export const content = css`
  ${sheetSection}
  grid-area: content;
  display: flex;
  flex-direction: column;
  gap: ${spacing.l};
  min-width: 0;

  ${media('<tablet')} {
    padding-bottom: ${spacing.xxl};
    padding-top: ${spacing.l};
  }

  ${media('>=tablet', '<=tablet-narrow')} {
    margin-top: calc(${spacing.l} - ${spacing.m});
  }

  ${media('>=desktop')} {
    max-width: ${MAIN_COLUMN_MAX_WIDTH}px;
  }
`;

export const sidebar = css`
  display: contents;

  ${media('>=desktop')} {
    grid-area: sidebar;
    display: flex;
    flex-direction: column;
    gap: ${spacing.m};
    margin-top: calc(-1 * (${spacing.l} + ${BANNER_OVERLAP}));
    position: relative;
    z-index: 1;
  }
`;

export const photo = css`
  grid-area: photo;

  ${media('<tablet')} {
    align-self: stretch;
    order: -1;
    position: sticky;
    top: ${MOBILE_BANNER_HEIGHT};
  }

  ${REDUCED_MOTION} {
    position: static;
  }

  ${media('>=tablet', '<desktop')} {
    align-self: start;
  }

  > div {
    aspect-ratio: 1;
    width: 100%;
  }
`;

export const divider = css`
  border-top: 1px solid ${colors.neutral.trim.default};
`;
